import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── Sensor panel (Equipment Detail) ───────────────────────────────────────
// Shown when the operator is a manager+. Lets them link a MOCREO probe
// to this equipment and tune the alarm thresholds. Renders a "Last
// reading: NN.N°F · M min ago" badge once linked.
export function SensorPanel({ equipment, currentUser, onChanged }) {
  const headers = currentUser ? { 'x-user-id': String(currentUser.id) } : {};
  const [sensors, setSensors] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [pick, setPick] = React.useState('');
  const [low, setLow] = React.useState(equipment.temp_low_f ?? '');
  const [high, setHigh] = React.useState(equipment.temp_high_f ?? '');
  // CJ feedback 2026-05-08: alerts on/off toggle. Default true (server-
  // side default). Stored on equipment.alerts_enabled.
  const [alertsOn, setAlertsOn] = React.useState(equipment.alerts_enabled !== false);

  async function loadStatus() {
    try {
      const r = await api('GET', `/api/equipment/${equipment.id}/sensor-status`, null, headers);
      setStatus(r);
    } catch (_) {}
  }
  async function loadSensors() {
    try {
      const r = await api('GET', '/api/sensors/available', null, headers);
      setSensors(r.sensors || []);
    } catch (e) {
      setSensors([]);
      setErr(e.message || 'Could not list sensors');
    }
  }
  React.useEffect(() => { loadStatus(); }, [equipment.id]);

  async function saveLink() {
    setBusy(true); setErr('');
    try {
      await api('PUT', `/api/equipment/${equipment.id}/sensor`, { sensor_id: pick || null }, headers);
      setPickerOpen(false);
      onChanged && onChanged();
      loadStatus();
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  }
  async function unlink() {
    if (!confirm('Unlink this sensor? The equipment will stop receiving readings.')) return;
    setBusy(true); setErr('');
    try {
      await api('PUT', `/api/equipment/${equipment.id}/sensor`, { sensor_id: null }, headers);
      onChanged && onChanged();
      loadStatus();
    } catch (e) { setErr(e.message || 'Unlink failed'); }
    finally { setBusy(false); }
  }
  async function saveThresholds() {
    setBusy(true); setErr('');
    try {
      await api('PUT', `/api/equipment/${equipment.id}/thresholds`, {
        temp_low_f:  low === '' ? null : Number(low),
        temp_high_f: high === '' ? null : Number(high),
      }, headers);
      onChanged && onChanged();
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  }
  async function toggleAlerts() {
    const next = !alertsOn;
    setAlertsOn(next);
    setBusy(true); setErr('');
    try {
      await api('PUT', `/api/equipment/${equipment.id}/alerts-enabled`,
        { enabled: next }, headers);
      onChanged && onChanged();
    } catch (e) {
      setAlertsOn(!next);  // rollback
      setErr(e.message || 'Toggle failed');
    } finally { setBusy(false); }
  }

  function relTime(iso) {
    if (!iso) return '—';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return 'just now';
    const m = Math.round(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} hr ago`;
    const d = Math.round(h / 24);
    return `${d} day${d === 1 ? '' : 's'} ago`;
  }

  const eq = status?.equipment || equipment;
  const last = status?.last_reading;
  const alarm = status?.alarm;
  const linked = !!eq.sensor_id;
  const stale = last && (Date.now() - new Date(last.logged_at).getTime() > 30 * 60 * 1000);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-ink">Sensor</h3>
          <div className="text-xs text-gray-500">
            {linked ? `Linked to ${eq.sensor_id}` : 'Not linked — pick a probe to auto-log temperatures'}
          </div>
        </div>
        {linked ? (
          <button onClick={unlink} disabled={busy}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
            Unlink
          </button>
        ) : (
          <button onClick={() => { setPickerOpen(true); if (!sensors) loadSensors(); }} disabled={busy}
            className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-lg hover:bg-blue-100">
            🔗 Link sensor
          </button>
        )}
      </div>

      {/* Reading badge */}
      {linked && last && (
        <div className="flex items-center gap-3 mb-3">
          <div className={`px-3 py-1.5 rounded-lg font-semibold text-sm ${
            stale ? 'bg-gray-100 text-gray-500' :
            (alarm?.alarm_active_since ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700')
          }`}>
            {Number(last.temperature).toFixed(1)}°F
          </div>
          <div className="text-xs text-gray-500">{relTime(last.logged_at)}</div>
          {stale && <div className="text-xs font-semibold text-amber-700">⚠ Sensor offline</div>}
          {alarm?.alarm_active_since && !stale && (
            <div className="text-xs font-semibold text-red-700">⚠ Out of range</div>
          )}
        </div>
      )}
      {linked && !last && (
        <div className="text-xs text-gray-500 italic mb-3">Waiting for first reading…</div>
      )}

      {/* Picker */}
      {pickerOpen && (
        <div className="mb-3 p-3 bg-blue-50 rounded-lg space-y-2">
          {sensors === null ? (
            <div className="text-xs text-gray-500">Loading sensors…</div>
          ) : sensors.length === 0 ? (
            <div className="text-xs text-amber-700">No sensors available — confirm a hub is linked for this location.</div>
          ) : (
            <>
              <select value={pick} onChange={e => setPick(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-ink bg-white">
                <option value="">— pick a sensor —</option>
                {sensors.map(s => (
                  <option key={s.sensor_id} value={s.sensor_id}>
                    {s.name || s.sensor_id} · {s.sensor_id}
                    {s.online === false ? ' (offline)' : ''}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <button onClick={saveLink} disabled={busy || !pick}
                  className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setPickerOpen(false)}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Alerts on/off — CJ feedback 2026-05-08 */}
      <div className="border-t border-gray-100 pt-3 mb-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={alertsOn} onChange={toggleAlerts} disabled={busy}
            style={{ marginTop: 2 }} />
          <span className="text-sm">
            <strong>Send alerts for this equipment</strong>
            <div className="text-xs text-gray-500" style={{ marginTop: 2 }}>
              When off: out-of-range / sensor-offline alerts are silenced. Thresholds
              stay configured. Useful for monitoring-only sensors or when this
              piece is being serviced. Alerts go to the location's manager(s) only.
            </div>
          </span>
        </label>
      </div>

      {/* Thresholds */}
      <div className="border-t border-gray-100 pt-3">
        <div className="text-xs text-gray-500 mb-2">
          Alert thresholds (°F). Out-of-range alerts fire after 2 consecutive polls past these values.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs">
            Low
            <input type="number" step="0.1" value={low} onChange={e => setLow(e.target.value)}
              className="ml-2 w-20 border border-gray-300 rounded px-2 py-1 text-sm bg-white" placeholder="33" />
          </label>
          <label className="text-xs">
            High
            <input type="number" step="0.1" value={high} onChange={e => setHigh(e.target.value)}
              className="ml-2 w-20 border border-gray-300 rounded px-2 py-1 text-sm bg-white" placeholder="41" />
          </label>
          <button onClick={saveThresholds} disabled={busy}
            className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700 disabled:opacity-50">
            Save thresholds
          </button>
        </div>
      </div>

      {err && <div className="mt-3 text-xs text-red-700 bg-red-50 p-2 rounded">{err}</div>}
    </div>
  );
}
